import { Menu } from "@headlessui/react";
import cx from "classnames";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/button";
import ColorPicker from "@/components/editor/preference-panel/global-colors/color-picker-loader";
import UpsellModal from "@/components/editor/preference-panel/global-fonts/upsell-modal-component";
import useTheme from "@/components/editor/themes/use-theme";
import InfoTooltip from "@/components/editor/tooltip/tooltip-component";
import {
  ButtonColorLabels,
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import { swallowEvent } from "@/components/event-boundary/event-boundary-component";
import Floating from "@/components/floating/floating-component";
import { ArrowDown } from "@/components/icons/arrow-down";
import { Tooltip } from "@/components/shared/tooltip";
import { useAdvancedFeatures } from "@/hooks/use-advanced-features";
import useAnalytics from "@/hooks/use-analytics";
import useProperties from "@/hooks/use-properties";

import colorPalettePreview from "./color-palette-preview.jpg";

export default function ThemeColorPalette() {
  const labelId = React.useId();
  const valueId = React.useId();
  const properties = useProperties();
  const { currentTheme } = useTheme();
  const [localColors, setLocalColors] = React.useState(() => properties.colors);
  const [isUpsellModalVisible, setUpsellModalVisible] = React.useState(false);
  const { advancedFeaturesEnabled } = useAdvancedFeatures();
  const { logAnalytics } = useAnalytics();
  const { t } = useTranslation();

  const localColorsRef = React.useRef(localColors);
  localColorsRef.current = localColors;
  const updateColors = React.useMemo(
    () =>
      debounce(() => {
        properties.updateColors(localColorsRef.current);
      }, 100),
    [properties]
  );

  const currentColorSetName = React.useMemo(() => {
    const colorSet = currentTheme.colorSet.find(({ colors }) =>
      isEqual(properties.colors, colors)
    );
    const colorSetTitle = colorSet?.title?.toLowerCase().split(" ").join("-");

    if (colorSetTitle === undefined) {
      return t("preference-panel.color-set-title.custom");
    }
    return t(`preference-panel.color-set-title.${colorSetTitle}`);
  }, [currentTheme.colorSet, t, properties.colors]);

  React.useEffect(() => {
    updateColors();
  }, [localColors, updateColors]);

  React.useEffect(() => {
    setLocalColors(properties.colors);
    // eslint-disable-next-line
  }, [currentTheme.kind]);

  return (
    <React.Fragment>
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center">
          <p id={labelId} className="mr-1 text-xs font-medium text-gray-700">
            {t("preference-panel.color-palette")}
          </p>
          <InfoTooltip
            heading={t("preference-panel.customize-colors")}
            description={t("preference-panel.custom-colors-description")}
            image={colorPalettePreview}
          />
        </div>
        {!advancedFeaturesEnabled && (
          <React.Fragment>
            <UpsellModal
              isShowing={isUpsellModalVisible}
              onClose={() => setUpsellModalVisible(false)}
            />
            <Button
              onClick={() => {
                setUpsellModalVisible(true);
                logAnalytics("CANVAS_EDITOR_UPGRADE_MODAL_SHOWN", {
                  upgrade_source: "custom_colors",
                });
              }}
              variant="ghost"
              isInline
              size="tiny"
              className="text-xs leading-5"
            >
              {t("preference-panel.edit-colors")}
            </Button>
          </React.Fragment>
        )}
      </div>
      <Menu as="div" className="relative">
        {({ open }) => (
          <React.Fragment>
            <div className="mb-2 grid w-full grid-cols-[1fr_max-content] grid-rows-[auto_auto] rounded border border-solid border-gray-300 bg-white">
              <Menu.Button
                data-testid="theme-color-palette-menu-button"
                aria-labelledby={`${labelId} ${valueId}`}
                className="col-[1/3] row-[1/3] grid h-[70px] w-full cursor-pointer grid-cols-[1fr_max-content] grid-rows-[auto_1fr] items-start border-0 bg-none p-3 text-left"
              >
                <p id={valueId} className="text-xs font-medium text-gray-700">
                  {currentColorSetName}
                </p>
                <span
                  aria-hidden
                  className="col-start-2 row-[1/3] self-center justify-self-end"
                >
                  <ArrowDown className={cx({ "rotate-180": open })} />
                </span>
              </Menu.Button>
              <div className="z-[var(--z-index-lowest)] col-[1/3] row-[2/3] mx-3 mb-3 mt-2 grid w-fit auto-cols-max grid-flow-col gap-1">
                {localColors.map((color, index) => {
                  return (
                    <Floating
                      key={index}
                      label="Color picker"
                      placement="top-start"
                      body={
                        <ColorPicker
                          color={localColors[index]}
                          onChange={(value) => {
                            setLocalColors((localColors) => {
                              return [
                                ...localColors.slice(0, index),
                                value.hex,
                                ...localColors.slice(index + 1),
                              ];
                            });
                          }}
                        />
                      }
                    >
                      {({ innerRef, triggerProps: _triggerProps }) => {
                        const triggerProps = advancedFeaturesEnabled
                          ? _triggerProps
                          : {};
                        const colorLabel = ColorLabels[index].toLowerCase();
                        return (
                          <Tooltip
                            key={index}
                            placement="top-center"
                            tooltip={t(
                              `preference-panel.color-labels.${colorLabel}`
                            )}
                          >
                            <button
                              type="button"
                              aria-label={t(
                                `preference-panel.color-labels.${colorLabel}`
                              )}
                              disabled={!advancedFeaturesEnabled}
                              className="size-6 appearance-none rounded-full border border-gray-300 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                              ref={(node) => {
                                innerRef.current = node;
                              }}
                              {...triggerProps}
                              onMouseDown={swallowEvent}
                              style={{
                                backgroundColor: color,
                              }}
                            />
                          </Tooltip>
                        );
                      }}
                    </Floating>
                  );
                })}
              </div>
            </div>
            <Menu.Items
              data-testid="theme-color-palette-menu-popover"
              className="absolute z-[var(--z-index-low)] max-h-[18.75rem] w-full max-w-72 overflow-auto rounded border border-gray-300 bg-white p-0 focus:outline-none"
            >
              {currentTheme.colorSet.map((item) => {
                const colorSetTitle = item.title
                  .toLowerCase()
                  .split(" ")
                  .join("-");
                return (
                  <Menu.Item key={item.title}>
                    {({ active }) => (
                      <button
                        data-testid="theme-color-palette-menu-item"
                        type="button"
                        className={cx(
                          "w-full overflow-hidden whitespace-nowrap bg-none px-3 py-3 text-left text-ellipsis",
                          { "bg-gray-100": active }
                        )}
                        onClick={() => {
                          setLocalColors(item.colors);
                        }}
                      >
                        <p className="mb-2 text-xs font-medium text-gray-700">
                          {t(
                            `preference-panel.color-set-title.${colorSetTitle}`
                          )}
                        </p>
                        <div className="grid auto-cols-max grid-flow-col gap-1">
                          {item.colors.map((color, index) => {
                            return (
                              <div
                                key={index}
                                className="size-6 rounded-full border border-gray-300"
                                style={{
                                  backgroundColor: color,
                                }}
                              />
                            );
                          })}
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.Items>
          </React.Fragment>
        )}
      </Menu>
    </React.Fragment>
  );
}

export function ButtonColorPalete({
  colorSet,
  onColorChange,
  defaultColorSet,
}: {
  colorSet: string[];
  onColorChange: (colors: string[]) => void;
  defaultColorSet?: string[];
}) {
  const properties = useProperties();

  const [localColors, setLocalColors] = React.useState(
    colorSet ??
      defaultColorSet ?? [
        properties.colors[0],
        //useInvertedColor is not a hook, but triggers eslint
        //eslint-disable-next-line react-hooks/rules-of-hooks
        useInvertedColor(properties.colors[0]),
        "transparent",
      ]
  );
  const { t } = useTranslation();

  const localColorsRef = React.useRef(localColors);
  localColorsRef.current = localColors;

  const updateColors = React.useMemo(
    () =>
      debounce(() => {
        onColorChange(localColorsRef.current);
      }, 100),
    [onColorChange]
  );

  React.useEffect(() => {
    updateColors();
  }, [localColors, updateColors]);

  return (
    <React.Fragment>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center">
          <p className="text-xs font-medium text-gray-700">
            {t("preference-panel.custom-styles")}
          </p>
        </div>
      </div>

      <div className="grid auto-cols-max grid-flow-col gap-1">
        {localColors.map((color, index) => {
          return (
            <Floating
              key={index}
              label="Color picker"
              placement="top-start"
              body={
                <ColorPicker
                  color={localColors[index]}
                  withPreset={false}
                  onChange={(value) => {
                    setLocalColors((colors) => {
                      return [
                        ...colors.slice(0, index),
                        value.hex,
                        ...colors.slice(index + 1),
                      ];
                    });
                  }}
                />
              }
            >
              {({ innerRef, triggerProps: _triggerProps }) => {
                const triggerProps = _triggerProps;
                const colorLabel = ButtonColorLabels[index].toLowerCase();
                return (
                  <Tooltip
                    key={index}
                    placement="top-center"
                    tooltip={t(`preference-panel.color-labels.${colorLabel}`)}
                  >
                    <button
                      type="button"
                      aria-label={t(
                        `preference-panel.color-labels.${colorLabel}`
                      )}
                      className="size-6 appearance-none rounded-full border border-gray-300 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                      ref={(node) => {
                        innerRef.current = node;
                      }}
                      {...triggerProps}
                      onMouseDown={swallowEvent}
                      style={{
                        backgroundColor: color,
                      }}
                    />
                  </Tooltip>
                );
              }}
            </Floating>
          );
        })}
      </div>
    </React.Fragment>
  );
}
